// src/components/FAQ.js
import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";

const CONTENT = {
  ar: {
    docTitle: "الأسئلة الشائعة",
    subtitle: "تطبيق ترستفيكس — TrustFix",
    intro: "إجابات عن أكثر الأسئلة تكراراً للعملاء ومقدّمي الخدمة.",
    groups: [
      {
        title: "العملاء",
        items: [
          ["كيف أحجز خدمة؟", "تصفح الخدمات المتاحة في الصفحة الرئيسية، واختر الفئة التي تريدها، ثم أكّد حجزك عن طريق دفع رسوم المنصة."],
          ["كيف تربطني ترستفيكس بمقدّم الخدمة؟", "يُرسَل طلبك في الوقت نفسه إلى أقرب مقدّمي الخدمة المتاحين عبر نظام الإسناد الفوري. ويحصل على المهمة أول من يقبلها خلال المدة المحددة، ويتّسع نطاق البحث تلقائياً إذا لم يقبلها أحد."],
          ["ما طرق الدفع المقبولة؟", "نقبل بطاقات الائتمان والخصم (فيزا، ماستركارد، مدى)، والمحفظة الرقمية (Apple Pay)."],
          ["هل تتم مشاركة موقعي مع مقدّم الخدمة؟", "تتم مشاركة معلومات محدودة مثل اسمك وموقعك العام لتمكين الخدمة. ويُستخدم الموقع لمطابقتك مع مقدّمي الخدمة القريبين وتمكين التنقّل."],
          ["هل يمكنني إلغاء حجزي؟", "نعم، يمكنك إلغاء الطلب قبل إسناد المهمة إلى مقدّم الخدمة؛ أما بمجرد إسناد المهمة إليه، فلن تتمكن من إلغائها."],
          ["طلب استرداد المبلغ؟", "لا يحق لك استرداد المبلغ إلا إذا قمت بإلغاء الطلب قبل تعيين مقدّم خدمة له. وسيتم إعادة المبلغ إلى رصيد محفظتك."],
          ["ما اللغات المتوفّرة في الدعم؟", "الدعم متاح باللغتين العربية والإنجليزية."],
        ],
      },
      {
        title: "مقدّمو الخدمة",
        items: [
          ["كيف أحقّق الأرباح كمقدّم خدمة؟", "تتّفق على قيمة الخدمة مباشرةً مع العميل وتحصّلها منه. وتتقاضى ترستفيكس رسم منصّةٍ ثابتاً عن كل طلبٍ يُسنَد إليك، يُدفَع مقدّماً عبر بوابة الدفع المعتمدة (Moyasar)."],
          ["هل تحدّد ترستفيكس أسعاري؟", "لا. المنصّة لا تحدّد أسعار الخدمات؛ تتّفق عليها مباشرةً مع العميل. وتتقاضى ترستفيكس رسم المنصّة فقط."],
          ["هل تضمن ترستفيكس حصولي على المبلغ من العميل؟", "لا. أنت وحدك المسؤول عن تحصيل قيمة الخدمة من العميل؛ ولا تضمن ترستفيكس تحصيل هذه المبالغ."],
          ["ما السلوكيات التي قد تؤدي إلى إيقاف حسابي؟", "قد يؤدي الاحتيال أو التلاعب أو إساءة تقديم الخدمة إلى إيقاف الحساب وغرامةٍ قدرها (1,000) ريال سعودي، إضافةً إلى المسؤولية عن الأضرار. وتشمل السلوكيات المحظورة أيضاً العمل تحت تأثير مؤثّر، والأعمال غير المرخّصة، والخدمات غير الآمنة أو غير القانونية."],
          ["هل أنا موظّف لدى ترستفيكس؟", "لا. مقدّمو الخدمة أطرافٌ مستقلّون، وليسوا وكلاء أو موظّفين، ولا يجوز لهم تقديم أنفسهم كممثّلين للمنصّة."],
          ["ما القانون الذي يحكم الاتفاقية؟", "تخضع الشروط لأنظمة المملكة العربية السعودية، وتُحال النزاعات إلى المحاكم السعودية المختصّة إذا لم تُحلّ ودّياً خلال (30) ثلاثين يوماً."],
        ],
      },
    ],
  },
  en: {
    docTitle: "Frequently Asked Questions",
    subtitle: "TrustFix",
    intro: "Answers to the most common questions from customers and providers.",
    groups: [
      {
        title: "Customers",
        items: [
          ["How do I book a service?", "Browse available services on the home page, select the category you want, and confirm your booking by paying the platform fees."],
          ["How does TrustFix match me with a provider?", "Your request is sent at the same time to the nearest available providers using instant dispatch. The first to accept within the set window gets the job, and the search area expands automatically if no one accepts."],
          ["What payment methods are accepted?", "We accept credit/debit cards (Visa, Mastercard, Mada), digital wallet (Apple Pay)."],
          ["Is my location shared with the provider?", "Limited information such as your name and general location is shared to enable the service. Location is used to match you with nearby providers and enable navigation."],
          ["Can I cancel my booking?", "Yes, you can cancel before the job is assigned to a tasker. Once the job is assigned to a tasker you can't cancel it."],
          ["Can I ask for a refund?", "You are only eligible for a refund if you cancelled the job before it was assigned to a tasker. The money will be refunded to your wallet balance."],
          ["What language is support available in?", "Support is available in both Arabic and English."],
        ],
      },
      {
        title: "Providers",
        items: [
          ["How do I earn as a provider?", "You agree the service price directly with the customer and collect it from them. TrustFix charges a fixed platform fee per assigned request, paid upfront via the approved gateway (Moyasar)."],
          ["Does TrustFix set my prices?", "No. The platform does not set service prices; you agree them directly with the customer. TrustFix only charges the platform fee."],
          ["Does TrustFix guarantee I'll get paid by the customer?", "No. You are solely responsible for collecting the service price from the customer; TrustFix does not guarantee collection."],
          ["What conduct can get my account suspended?", "Fraud, manipulation, or service abuse can lead to suspension and a penalty of SAR 1,000, plus liability for damages. Prohibited conduct also includes working while impaired, unlicensed work, and unsafe or illegal services."],
          ["Am I an employee of TrustFix?", "No. Providers are independent parties, not agents or employees, and may not present themselves as representatives of the platform."],
          ["What law governs the agreement?", "The Terms are governed by the laws of the Kingdom of Saudi Arabia, with disputes referred to the competent Saudi courts if not resolved amicably within 30 days."],
        ],
      },
    ],
  },
};

export default function FAQ() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("tf_lang") || "ar";
    } catch {
      return "ar";
    }
  });

  const isAr = lang === "ar";
  const t = CONTENT[lang];

  // track open item by "groupIndex-itemIndex"
  const [open, setOpen] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem("tf_lang", lang);
    } catch {}
  }, [lang]);

  const toggle = (key) => setOpen((cur) => (cur === key ? null : key));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header lang={lang} setLang={setLang} />
      <div className="min-h-screen" dir={isAr ? "rtl" : "ltr"}>
        <section>
          <div className="max-w-4xl mx-auto px-5 pt-3 pb-3">
            <h1 className="text-[26px] font-extrabold text-[#006C35] mt-2 mb-1">
              {t.docTitle}
            </h1>
            <p className="text-gray-500 text-[15px] mt-0 mb-6">{t.subtitle}</p>
          </div>
        </section>

        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-5 pt-8 pb-12">
            <p className="text-gray-700 mb-8 leading-relaxed">{t.intro}</p>

            {t.groups.map((group, gi) => (
              <div key={group.title} className="mb-8">
                <h2 className="text-lg font-bold text-[#006C35] mb-4">
                  {group.title}
                </h2>

                <div className="space-y-3">
                  {group.items.map(([q, a], ii) => {
                    const key = `${gi}-${ii}`;
                    const isOpen = open === key;
                    return (
                      <div
                        key={key}
                        className="border border-gray-200 rounded-xl overflow-hidden bg-white"
                      >
                        <button
                          onClick={() => toggle(key)}
                          aria-expanded={isOpen}
                          className={`w-full flex items-center justify-between gap-3 px-5 py-4 text-start transition-colors ${
                            isOpen ? "bg-[#EDEEF2]" : "hover:bg-gray-50"
                          }`}
                        >
                          <span className="font-semibold text-gray-800">
                            {q}
                          </span>
                          <ChevronDown
                            size={20}
                            className={`shrink-0 text-[#535C79] transition-transform duration-200 ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isOpen && (
                          <div className="px-5 pb-4 pt-1 text-gray-600 leading-relaxed">
                            {a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <Footer lang={lang} />
    </div>
  );
}